/**
 * Immutable config registry with deep-freeze and scoped overrides
 * Keeps config read-only at runtime; supports test overrides
 */

type Primitive = string | number | boolean | null | undefined | symbol | bigint;
type Freezable = Primitive | Freezable[] | { [k: string]: Freezable };

function deepFreeze<T extends Freezable>(o: T): T {
  if (o && typeof o === "object") {
    Object.freeze(o);
    if (Array.isArray(o)) {
      for (const v of o) deepFreeze(v as Freezable);
    } else {
      for (const k of Object.keys(o))
        deepFreeze((o as Record<string, Freezable>)[k]);
    }
  }
  return o;
}

let CURRENT: Record<string, Freezable> = {};

export function createConfig<T extends Record<string, Freezable>>(
  ns: string,
  value: T,
): Readonly<T> {
  const frozen = deepFreeze({ ...value });
  CURRENT = { ...CURRENT, [ns]: frozen };
  return frozen as Readonly<T>;
}

export function getConfig<T extends Record<string, Freezable>>(
  ns: string,
): Readonly<T> | undefined {
  const v = CURRENT[ns];
  if (!v) return undefined;
  return v as Readonly<T>;
}

/**
 * Test-only scoped override. No-ops in production.
 */
export function overrideConfig<T extends Record<string, Freezable>>(
  ns: string,
  next: Partial<T>,
): Readonly<T> {
  const isProduction =
    typeof process !== "undefined" && process.env?.NODE_ENV === "production";
  if (isProduction) {
    const existing = getConfig<T>(ns);
    if (!existing) throw new Error(`Config namespace "${ns}" not found`);
    return existing;
  }
  const merged = { ...(CURRENT[ns] as object), ...next } as T;
  return createConfig<T>(ns, merged);
}

/**
 * Test-only reset. Clears all configs. No-op in production.
 */
export function resetForTests(): void {
  const isProduction =
    typeof process !== "undefined" && process.env?.NODE_ENV === "production";
  if (isProduction) return;
  CURRENT = {};
}
